<template>
	<ArrowImageGallery
		v-if="images?.length > 1 && bigImageIndexRef"
		arrow-direction="left"
		:class="[`${showPopUp && 'ml-4'}`]"
		:number-of-slides="images?.length"
		@click="decreaseBigImageIndex(bigImageIndexRef - 1)"
	/>
	<div
		ref="bigPictureCarouselRef"
		class="flex overflow-x-auto lg:no-scrollbar lg:h-full items-center"
	>
		<div
			v-for="(image, index) in images"
			:id="`${!showPopUp && 'bigImagesGallery'}`"
			:key="imageId(image) + size"
			ref="bigImagesGallery"
			data-testid="product-gallery-slide"
			:class="[
				`flex ${size ? 'min-w-full' : 'min-w-[568px]'} relative max-h-full `
			]"
		>
			<div
				:ref="
					(el) => {
						imagesRef[index] = {
							[size]: el as HTMLElement
						};
					}
				"
				:class="[`${zoomedInPopUpPictureWrapper} w-[100%] justify-center `]"
			>
				<div
					:class="[
						` ${zoomedInPopUpPictureChild} flex min-w-[90%] h-full absolute left-[50%] -translate-x-[50%]`
					]"
					data-testid="product-gallery-zoom"
					@click="doAction"
				></div>
				<!-- Video slides (uploaded mp4 / embedded YouTube). -->
				<ProductGalleryVideo
					v-if="!isProductImage(image)"
					:file="image as ProductFile"
					class="w-full h-full"
				/>
				<!-- Show big image on zoom.
				@nuxt/image keys an un-prefixed `sizes` value to the "1px" screen, so a
				bare "100vw" collapses the srcset to `?w=1`/`?w=2` and the Norce CDN
				honours it (w=1 returns a 1x2px webp). Always prefix every value with a
				screen - here and on the NuxtImg below. -->
				<NuxtImg
					v-else-if="showPopUp"
					provider="norce"
					format="webp"
					:src="imagePath(image)"
					:alt="$t('image_fallback_product')"
					:preload="index != 0 ? false : true"
					:loading="index != 0 ? 'lazy' : 'eager'"
					:fetchpriority="index === 0 ? 'high' : undefined"
					:class="`  h-auto w-auto  object-center`"
					sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw"
				/>
				<!-- Show small image otherwise.
				Norce CDN images go through a plain <img> with a hand-built srcset
				(see utils/product/norceImage.ts): that leaves one mechanism deciding
				what the browser downloads. NuxtImg also emits a
				`<link rel="preload" imagesrcset>` beside its own provider srcset, and
				the two can settle on different candidates — the same photo then
				downloads twice. Same approach breadandboxers runs in production.
				NuxtImg stays below for anything not on the Norce CDN. -->
				<img
					v-else-if="isNorceCdnUrl(imagePath(image))"
					:src="appendParams(imagePath(image)!, 'format=webp')"
					:srcset="
						buildNorceSrcSet(appendParams(imagePath(image)!, 'format=webp'))
					"
					sizes="(max-width: 1024px) 100vw, 60vw"
					:alt="$t('image_fallback_product')"
					:loading="index != 0 ? 'lazy' : 'eager'"
					:fetchpriority="index === 0 ? 'high' : 'auto'"
					:class="` object-cover h-full w-full  object-center lg:h-full lg:w-full`"
					width="800"
					height="800"
				/>
				<NuxtImg
					v-else
					provider="norce"
					format="webp"
					:src="imagePath(image)"
					:alt="$t('image_fallback_product')"
					:preload="index != 0 ? false : true"
					:loading="index != 0 ? 'lazy' : 'eager'"
					:fetchpriority="index === 0 ? 'high' : undefined"
					:class="` object-cover h-full w-full  object-center lg:h-full lg:w-full`"
					sizes="xs:100vw sm:100vw md:100vw lg:60vw xl:60vw xxl:60vw"
					width="800"
					height="800"
				/>
			</div>
		</div>
	</div>
	<ArrowImageGallery
		v-if="images?.length > 1 && bigImageIndexRef < images?.length - 1"
		:number-of-slides="images?.length"
		arrow-direction="right"
		:class="[`${showPopUp && 'mr-4'}`]"
		@click="increaseBigImageIndex(bigImageIndexRef + 1)"
	/>
</template>

<script setup lang="ts">
import { toRef } from "vue";
import { useResizeObserver } from "@vueuse/core";
import type { ProductFile } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { CategoryFiles } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { FilteredProductItemFiles } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type {
	SetShowPopUp,
	BigImageIndex
} from "~/types/storyblok/imageGallery/imageGallery";
import { isProductImage } from "~/utils/product/productMedia";
import {
	appendParams,
	buildNorceSrcSet,
	isNorceCdnUrl
} from "~/utils/product/norceImage";

// No `placeholder` on the slides below: NuxtImg drops `srcset`/`sizes` from the
// <img> while a placeholder is set, so the parser downloads the placeholder URL
// AND the preload link downloads the real srcset candidate — two full images,
// since the Norce provider ignores blur/height/quality modifiers and hands back
// the full-size file. Without it, SSR emits the real src + srcset and the
// browser fetches exactly one.

interface Props {
	images: ProductFile[] | CategoryFiles[] | FilteredProductItemFiles[];
	bigImageIndex: number;
	decreaseBigImageIndex: BigImageIndex;
	increaseBigImageIndex: BigImageIndex;
	size: string;
	setShowPopUp?: SetShowPopUp;
	showPopUp?: boolean;
}

const {
	images,
	bigImageIndex,
	decreaseBigImageIndex,
	increaseBigImageIndex,
	size,
	setShowPopUp,
	showPopUp
} = defineProps<Props>();

const bigImageIndexRef = toRef(() => bigImageIndex);
// Starts as an empty array, not `undefined`: the watchEffect below emits this
// ref before the template refs are populated, and a consumer that gates
// rendering on the emitted value would otherwise unmount on the first emit.
const bigImagesGallery = ref<HTMLElement[]>([]);
const imagesRef = ref<{ [key: string]: HTMLElement }[]>([]);
const bigPictureCarouselRef = ref<HTMLElement>();

const zoomedInPopUpPictureWrapper = ref("flex");
const zoomedInPopUpPictureChild = ref("cursor-zoom-in");
const slideToImage = ({
	displayedBigImage,
	smooth
}: {
	displayedBigImage: number;
	smooth?: boolean;
}) => {
	if (Object.keys(imagesRef.value).length) {
		if (bigPictureCarouselRef.value) {
			const stepSize =
				imagesRef?.value?.[displayedBigImage]?.[size]?.getBoundingClientRect()
					?.width;
			if (smooth) {
				bigPictureCarouselRef.value.classList.add("scroll-smooth");
			}
			const scrollLeft = stepSize! * displayedBigImage;
			bigPictureCarouselRef.value.scrollLeft = scrollLeft;
		}
	}
};

onMounted(() => {
	slideToImage({ displayedBigImage: bigImageIndex });
});

const doAction = () => {
	if (!showPopUp) {
		if (setShowPopUp) {
			setShowPopUp(true);
		}
		zoomedInPopUpPictureWrapper.value = "";

		const body = document.getElementsByTagName("body")[0];
		if (body) {
			body.style.overflow = "hidden";
		}

		zoomedInPopUpPictureChild.value = "max-h-[2600px] cursor-zoom-out";
	} else {
		if (zoomedInPopUpPictureWrapper.value === "flex") {
			zoomedInPopUpPictureChild.value = "max-h-[2600px] cursor-zoom-out";

			return (zoomedInPopUpPictureWrapper.value = "");
		} else {
			zoomedInPopUpPictureWrapper.value = "flex";
			zoomedInPopUpPictureChild.value = "cursor-zoom-in";
		}
	}
};

const emits = defineEmits(["bigImagesGalleryRef"]);
watchEffect(() => {
	slideToImage({
		displayedBigImage: bigImageIndex,
		smooth: true
	});

	emits("bigImagesGalleryRef", { bigImagesGallery });
});

useResizeObserver(bigPictureCarouselRef, () => {
	slideToImage({
		displayedBigImage: bigImageIndex,
		smooth: true
	});
});

const { isCategoryFile, isFilteredProductItemFiles } = useNorcePwa();

const imagePath = (
	image: ProductFile | CategoryFiles | FilteredProductItemFiles
) => {
	return isCategoryFile(image) ? image?.ImagePath : image?.Path;
};

const imageId = (
	image: ProductFile | CategoryFiles | FilteredProductItemFiles
) => {
	return isCategoryFile(image) || isFilteredProductItemFiles(image)
		? image?.ImageKey
		: image?.Key;
};
</script>
